import type { JSX } from 'react';
import { useState } from 'react';

import type { Theme } from '@/types';

import { useSeo } from '@hooks/useSeo';
import { seoHome } from '@data/seo';
import { useLang, useT } from '@/i18n';

import IntroSplash from '@components/intro/IntroSplash';
import Navbar from '@components/nav/Navbar';
import Hero from '@components/hero/Hero';
import Pillars from '@components/sections/Pillars';
import Rupture from '@components/sections/Rupture';
import ExpertiseTerrain from '@components/sections/ExpertiseTerrain';
import CncApps from '@components/sections/CncApps';
import AsterionApps from '@components/sections/AsterionApps';
import AiMaturity from '@components/sections/AiMaturity';
import Projects from '@components/sections/Projects';
import Articles from '@components/sections/Articles';
import Skills from '@components/sections/Skills';
import Timeline from '@components/sections/Timeline';
import Contact from '@components/sections/Contact';

const INTRO_SESSION_KEY = 'ps_intro_seen';

function shouldShowIntro(): boolean {
  // Pendant la capture react-snap, l'intro masquerait tout le contenu :
  // la page pre-rendue ne contiendrait que le splash, invisible pour Google.
  if (navigator.userAgent === 'ReactSnap') return false;
  return sessionStorage.getItem(INTRO_SESSION_KEY) !== '1';
}

interface HomeProps {
  theme: Theme;
  onToggleTheme: () => void;
}

export default function Home({ theme, onToggleTheme }: HomeProps): JSX.Element {
  const [introVisible, setIntroVisible] = useState<boolean>(shouldShowIntro);
  const lang = useLang();
  const t = useT();

  useSeo(seoHome(lang));

  function handleIntroDismiss(): void {
    sessionStorage.setItem(INTRO_SESSION_KEY, '1');
    setIntroVisible(false);
    // L'ancre demandee (/#section) n'existait pas tant que l'intro masquait
    // le contenu : on rejoue le scroll une fois les sections montees.
    const { hash } = window.location;
    if (hash !== '') {
      requestAnimationFrame(() => {
        document.querySelector(hash)?.scrollIntoView();
      });
    }
  }

  return (
    <>
      {introVisible && <IntroSplash onDismiss={handleIntroDismiss} />}

      {!introVisible && (
        <>
          <Navbar theme={theme} onToggleTheme={onToggleTheme} />
          <main>
            <Hero />
            <Pillars />
            <Rupture />
            <ExpertiseTerrain />
            <CncApps />
            <AsterionApps />
            <AiMaturity />
            <Projects />
            <Articles />
            <Skills />
            <Timeline />
            <Contact />
          </main>
          <footer className="SiteFooter">
            <span className="SiteFooter__text">© 2026 Pierre Séré · pierresere.com</span>
            <span className="SiteFooter__text">{t.footer.builtWith}</span>
          </footer>
        </>
      )}
    </>
  );
}
